import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import VisibilitySensor from 'react-visibility-sensor';
import {
  isPreviewPass,
  PLAYER_REFERENCE_BANNER,
  PLAYER_REFERENCE_GLOBAL,
  playerPlay,
} from 'helpers/PlayerHelper';
import { helperGoToLiveNews } from 'helpers/RouteHelper';
import { noop } from 'helpers/GlobalHelper';
import {
  Events,
  getSegmentModelMetadata,
  handleAnalytics,
  SegmentPageActionTypes,
  SegmentSources,
  SegmentUiElements,
} from '@foxcorp/delta-web-analytics';
import { getLiveVideo } from 'components/VideoPlayerGlobal/Helper';
import BannerDetail from './components/BannerDetail';
import Header from './components/Header';
import BannerImage from './components/BannerImage';
import BannerVideo from './components/BannerVideo';
import Style from './VideoBanner.module.scss';

class VideoBanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      nextVideo: null,
    };
  }

  updateBannerVideo = nextVideo => {
    if (!nextVideo) return;
    this.setState({ nextVideo });
  };

  handleBannerClick = ({ headline, id, isAuthenticated, video }) => {
    const {
      auth_signInHeader: signInWithProviderText,
      global_watchButton: watchButtonText,
    } = window.Language;
    const { name, seriesName } = video;
    const type = isAuthenticated ? watchButtonText : signInWithProviderText;
    handleAnalytics({
      event: Events.BANNER_NOTIFICATION_ACTION,
      payload: {
        page_message_id: id,
        page_message_title: headline,
        page_message_text: seriesName || name,
        page_message_type: type,
        page_action_type: type,
      },
    });
  };

  handleVideoImageClick = video => {
    const { collectionCount, panel, verticalPosition } = this.props;
    const modelMetaData = video?.trackingData?.properties?.modelMetaData;
    handleAnalytics({
      event: Events.PLAYLIST_BUTTON_ACTION,
      payload: {
        page_action_location: SegmentSources.HOME,
        page_action_type: SegmentPageActionTypes.WATCH,
        page_collection_count: collectionCount,
        page_collection_id: panel?.id,
        page_collection_title: panel?.headline,
        page_item_horizontal_position: 1,
        page_item_vertical_position: verticalPosition,
        page_item_title: video?.headline,
        page_item_subtitle: video?.description,
        page_playlist_length: 1,
        page_ui_element: SegmentUiElements.PLAY,
        page_video_uid: video?.uID,
        page_recs_collection: panel?.recommended,
        page_recs_collection_model_id: modelMetaData?.modelId ?? ' ',
        page_recs_collection_request_id: modelMetaData?.requestId ?? ' ',
        page_recs_collection_result_set_id: modelMetaData?.resultSetId ?? ' ',
        page_recs_collection_source_id: modelMetaData?.sourceId ?? ' ',
        ...getSegmentModelMetadata(),
      },
    });
    helperGoToLiveNews();
  };

  render() {
    const {
      accessToken,
      account,
      anonymousAccount,
      anonymousId,
      anonymousLogin,
      authSuccess,
      breakpoint,
      defaultVideoContainerProps,
      deviceId,
      mvpd,
      onAuthCheckComplete,
      panel,
      preauthorizedResources,
      setUserViewedPlayerBanner,
      toggleMvpdAuthOverlay,
      userViewedPlayerBanner,
    } = this.props;
    const { nextVideo } = this.state;
    const videos = _.get(panel, 'items.member');
    const video = getLiveVideo({ nextVideos: videos });
    if (!video) return null;

    const headline = _.get(panel, 'headline');
    const id = _.get(panel, 'id');
    const playerReference = `${PLAYER_REFERENCE_BANNER}-${id}`;
    const isAuthenticated = !!mvpd;
    let containerClasses = [Style.container];
    let tvProviderOnClick = () => {
      this.handleBannerClick({ headline, id, isAuthenticated, video });
      const options = {
        onAuthCheckComplete,
        onOverlayClose: () => {
          toggleMvpdAuthOverlay(false);
        },
      };
      toggleMvpdAuthOverlay(true, options);
    };

    if (isAuthenticated) {
      containerClasses = [Style.container];
      tvProviderOnClick = _.noop;
    }

    const onVisibilityChange = isVisible => {
      if (isVisible) setUserViewedPlayerBanner(true);
      if (isAuthenticated) {
        const activePlayer = isVisible ? playerReference : PLAYER_REFERENCE_GLOBAL;
        playerPlay(activePlayer);
      }
    };

    const shouldRenderBannerVideo = isPreviewPass(mvpd) || isAuthenticated;

    return (
      <div className={Style.videoBannerContainer}>
        <Header headline={headline} />
        <VisibilitySensor onChange={onVisibilityChange}>
          <div className={classNames(containerClasses)}>
            {shouldRenderBannerVideo ? (
              <BannerVideo
                accessToken={accessToken}
                account={account}
                anonymousAccount={anonymousAccount}
                anonymousId={anonymousId}
                anonymousLogin={anonymousLogin}
                authSuccess={authSuccess}
                breakpoint={breakpoint}
                defaultVideoContainerProps={defaultVideoContainerProps}
                deviceId={deviceId}
                playerReference={playerReference}
                preauthorizedResources={preauthorizedResources}
                updateBannerVideo={this.updateBannerVideo}
                userViewedPlayerBanner={userViewedPlayerBanner}
                video={video}
              />
            ) : (
              <BannerImage
                breakpoint={breakpoint}
                isAuthenticated={isAuthenticated}
                onVideoImageClick={() => this.handleVideoImageClick(video)}
                video={video}
              />
            )}
            <BannerDetail
              breakpoint={breakpoint}
              isAuthenticated={isAuthenticated}
              onTvProviderClick={tvProviderOnClick}
              video={nextVideo || video}
            />
          </div>
        </VisibilitySensor>
      </div>
    );
  }
}

export default VideoBanner;

VideoBanner.defaultProps = {
  accessToken: null,
  account: null,
  anonymousAccount: null,
  anonymousId: null,
  authSuccess: false,
  defaultVideoContainerProps: {},
  deviceId: null,
  mvpd: null,
  onAuthCheckComplete: noop,
  panel: null,
  preauthorizedResources: [],
  setUserViewedPlayerBanner: noop,
  toggleMvpdAuthOverlay: noop,
  userViewedPlayerBanner: false,
};

VideoBanner.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  anonymousAccount: PropTypes.shape({}),
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func.isRequired,
  authSuccess: PropTypes.bool,
  breakpoint: PropTypes.string.isRequired,
  collectionCount: PropTypes.number.isRequired,
  defaultVideoContainerProps: PropTypes.shape({}),
  deviceId: PropTypes.string,
  mvpd: PropTypes.string,
  onAuthCheckComplete: PropTypes.func,
  panel: PropTypes.shape({}),
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  setUserViewedPlayerBanner: PropTypes.func,
  toggleMvpdAuthOverlay: PropTypes.func,
  userViewedPlayerBanner: PropTypes.bool,
  verticalPosition: PropTypes.number.isRequired,
};
